import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormFeedback, FormField, Select } from '~/components';
import { STATES } from '~/constants';
import { createFormFieldStyles } from '~/constants/styles';

interface Props {
  name: string;
  countryFieldName: string;
  label?: string;
  cyTag: string;
  required?: boolean;
  usePortal?: boolean;
  errorCyTag?: string;
  reserveFeedbackSpace?: boolean;
  maxProvinceLength?: number;
}

const EMPTY_OPTION = { value: '', label: '' };

// State/Province input that swaps shape based on the sibling country field.
// US -> existing STATES Select. Anything else -> free-text input that writes
// {value: text, label: text} to the same field so the address schema's
// optionSchema stays satisfied without needing a separate column.
export const StateOrProvinceInput = ({
  name,
  countryFieldName,
  label,
  cyTag,
  required,
  usePortal,
  errorCyTag,
  reserveFeedbackSpace,
  maxProvinceLength = 2,
}: Props) => {
  const { control, setValue, formState } = useFormContext();
  const country = useWatch({ control, name: countryFieldName }) as { value?: string } | undefined;
  const countryValue = country?.value ?? '';
  const isUs = !countryValue || countryValue === 'US';

  // Clear the stored stateCode when the user changes the country -- but only
  // when the change is user-initiated, not a programmatic reset (e.g. the
  // wizard's AsyncSelect snapping the form to a selected Canadian facility
  // brings its own stateCode and shouldn't be wiped). RHF's dirtyFields tree
  // tracks which paths the user has touched; a reset clears it.
  const previousCountryRef = useRef<string | null>(null);
  const isCountryDirty =
    countryFieldName
      .split('.')
      .reduce<unknown>(
        (acc, key) => (acc as Record<string, unknown> | undefined)?.[key],
        formState.dirtyFields
      ) === true;
  useEffect(() => {
    if (
      previousCountryRef.current !== null &&
      previousCountryRef.current !== countryValue &&
      isCountryDirty
    ) {
      setValue(name, EMPTY_OPTION, { shouldDirty: true, shouldValidate: false });
    }
    previousCountryRef.current = countryValue;
  }, [countryValue, isCountryDirty, name, setValue]);

  if (isUs) {
    return (
      <Select
        name={name}
        label={label ?? 'State'}
        cyTag={cyTag}
        options={STATES}
        required={required}
        usePortal={usePortal}
        errorCyTag={errorCyTag}
        reserveFeedbackSpace={reserveFeedbackSpace}
      />
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const display = (field.value as { label?: string } | undefined)?.label ?? '';
        const error = fieldState.error;
        return (
          <FormField
            id={name}
            label={label ?? 'Province / Region'}
            required={required}
            tooltip={
              maxProvinceLength === 2
                ? '2-letter subdivision code (e.g. ON for Ontario, BC for British Columbia, BY for Bavaria). Limited to 2 characters to match the address database column. Regions whose standard code is longer than 2 characters (e.g. NSW for New South Wales) can use their 2-letter ISO 3166-2 alpha-2 code or a 2-letter abbreviation.'
                : undefined
            }
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={`${name}-error`}
                errorCyTag={errorCyTag ?? `${cyTag}-error`}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag}>
              <input
                id={name}
                value={display}
                maxLength={maxProvinceLength}
                onChange={(e) => {
                  const raw =
                    maxProvinceLength === 2 ? e.target.value.toUpperCase() : e.target.value;
                  const next = raw.slice(0, maxProvinceLength);
                  field.onChange(next ? { value: next, label: next } : { value: '', label: '' });
                }}
                onBlur={field.onBlur}
                className={classNames(createFormFieldStyles(false, false, !!error))}
                aria-invalid={!!error || undefined}
                aria-required={required || undefined}
                aria-describedby={error?.message ? `${name}-error` : undefined}
                aria-errormessage={error?.message ? `${name}-error` : undefined}
                placeholder={maxProvinceLength === 2 ? 'ON' : undefined}
                autoCapitalize={maxProvinceLength === 2 ? 'characters' : 'none'}
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
